import React, { useEffect, useMemo } from 'react';

import Loading from '@/components/common/Loading';
import Tab from '@/components/common/Tab';
import { TabItem } from '@/components/common/Tab';
import { UserIdentity } from '@/components/RightProvider/const';
import { LimitConfig } from '@/components/RightProvider/type';
import { useTranslate, UseTranslateType } from '@/hooks/useTranslate';
import { DateType } from '@/store/Payment';
import UserStore from '@/store/User';
import { AIFunction } from '@/utils/const';
import { SubscribeReasonEnum } from '@/utils/eventlog/eventNewConfig';

import ComboContent from './components/comboPanel';
import Footer from './components/footer';
import usePayment from './hook/usePayment';
import BenefitIcon from './icon/benefit';
import CloseIcon from './icon/close';

export interface PaymentModalProps {
  t?: UseTranslateType;
  customTip?: string;
  autoNext?: boolean;
  feature: AIFunction;
  activeTab?: DateType;
  onOk?: () => void;
  onClose?: () => void;
  destroyRef?: any;
  reason?: SubscribeReasonEnum;
}

export interface TabContentProps {
  activeKey: DateType;
  disCount: number;
  disabled: boolean;
  onChange: (value: DateType) => void;
}

const Title = ({
  customTip,
  onClose,
  t,
}: Pick<PaymentModalProps, 'customTip' | 'onClose' | 't'>) => (
  <div className="flex items-center justify-center px-[16px] pt-[18px] pb-[10px] relative">
    {customTip ? (
      <div
        className="absolute top-[10px] bg-app-card border border-app-accent px-[68px] h-[48px] flex items-center justify-center rounded-[12px] gap-[10px] animate-fadeInOutPaymentTip opacity-0 z-[10] shadow-[0_18px_48px_rgba(139,92,246,0.24)]"
        style={{
          animationDelay: '0.1s',
        }}
      >
        <BenefitIcon />
        <span className="text-[18px] font-[500]">{customTip}</span>
      </div>
    ) : null}

    <div
      className={`w-[1080px] relative text-center font-sans text-app-primary ${customTip ? 'animate-fadeInOutPayment opacity-0' : ''}`}
      style={{ animationDelay: customTip ? '1s' : '0s' }}
    >
      <div className="text-[32px] font-[700] leading-[38px] tracking-[0px]">
        {t && t('merror.sub.pricing')}
      </div>
      <div className="mx-auto mt-[7px] text-[13px] leading-[20px] text-app-secondary">
        {t && t('bc.compare')}
      </div>
      <div
        className="fixed top-[30px] right-[30px] flex flex-row items-center justify-center cursor-pointer z-[10] text-app-secondary hover:text-app-primary transition-colors"
        onClick={onClose}
      >
        <CloseIcon />
      </div>
    </div>
  </div>
);
const TabContent = ({ activeKey, disCount, disabled, onChange }: TabContentProps) => {
  const { t } = useTranslate();

  let items: TabItem<DateType>[] = [
    // {
    //   key: 'twoweek',
    //   label: () => {
    //     return <span className="px-[30px]">{t('Time-limited-offer')}</span>;
    //   },
    // },
    {
      key: 'month',
      label: (activeKey) => {
        return (
          <span
            className={`px-[50px] ${activeKey === 'month' ? 'text-app-primary font-[500]' : 'text-app-tertiary'}`}
          >
            {t('merror.sub.month')}
          </span>
        );
      },
    },
    {
      key: 'year',
      label: () => {
        return (
          <div
            className={`px-[20px] flex items-center gap-[8px] ${activeKey === 'year' ? 'text-app-primary font-[500]' : 'text-app-tertiary'}`}
          >
            <span>{t('merror.sub.year')}</span>
            {disCount > 0 ? (
              <span className="inline-flex items-center rounded-full bg-[#FDE043] px-[8px] py-[2px] text-[11px] font-[700] leading-[14px] text-[#171717] shadow-[0_6px_14px_rgba(253,224,67,0.2)]">
                {t('fc.saveUp')} {disCount}%
              </span>
            ) : null}
          </div>
        );
      },
    },
  ];
  return (
    <div className="px-[28px] pb-[12px] flex justify-center">
      <Tab
        disabled={disabled}
        className="bg-app-card rounded-[8px] p-[4px] shrink-0 border border-app"
        itemClassName="py-[6px] text-[18px]"
        sliderClassName="bg-app-card-active rounded-[8px]"
        type="slider"
        items={items}
        activeKey={activeKey}
        onChange={onChange}
      />
    </div>
  );
};

export const PaymentContentUS: React.FC<PaymentModalProps> = ({
  customTip,
  feature,
  activeTab: defaultActiveTab,
  autoNext = false,
  onClose,
  onOk,
  t,
  destroyRef,
  reason,
}: PaymentModalProps) => {
  const purchaseLimitLoading = UserStore((store) => store.purchaseLimitLoading);

  const {
    handlePay,
    activeTab,
    highlightBenefit,
    formatData,
    weightData,
    loadingStates,
    handleTabChange,
    isPurchaseSuccess,
  } = usePayment({
    t,
    defaultActiveTab,
    feature,
    reason,
  });

  useEffect(() => {
    if (isPurchaseSuccess) {
      onClose && onClose();
      if (autoNext) {
        onOk && onOk();
      }
    }
  }, [isPurchaseSuccess]);

  const onClick = async (
    key: UserIdentity,
    item: LimitConfig[keyof LimitConfig],
    btnIndex: number,
    price: string,
    currency: string,
    selectedCommodityCode: string,
  ) => {
    await handlePay({
      key,
      item,
      btnIndex,
      feature,
      price,
      currency,
      selectedCommodityCode,
    });
  };

  return (
    <div className="bg-app top-0 left-0 relative min-h-screen overflow-hidden">
      <div
        className="absolute"
        style={{
          left: '15%',
          top: '-30%',
          width: '705px',
          height: '705px',
          borderRadius: '705px',
          background: 'rgba(139, 92, 246, 0.28)',
          filter: 'blur(210px)',
        }}
      ></div>

      <div
        className="absolute"
        style={{
          right: '13%',
          top: '-30%',
          width: '705px',
          height: '705px',
          borderRadius: '705px',
          background: 'rgba(56, 189, 248, 0.12)',
          filter: 'blur(210px)',
        }}
      ></div>

      <div
        className="flex flex-col overflow-auto py-[10px] relative z-10 bg-transparent"
        style={{ height: '100vh', width: '100vw' }}
      >
        <Title customTip={customTip} onClose={onClose} t={t} />
        <div className="relative mx-auto w-fit max-w-[calc(100vw-56px)] rounded-[18px] px-[14px] pb-[14px] pt-[14px] backdrop-blur">
          {purchaseLimitLoading ? (
            <div className="min-h-[400px] relative">
              <Loading size={60} />
            </div>
          ) : null}
          <TabContent
            disabled={loadingStates.some((state) => state)}
            activeKey={activeTab}
            onChange={(value) => {
              handleTabChange(value);
            }}
            disCount={highlightBenefit.disCount}
          />

          <ComboContent
            t={t}
            formatData={formatData}
            weightData={weightData}
            activeDateType={activeTab}
            highlightBenefit={highlightBenefit.type}
            loadingStates={loadingStates}
            onClick={onClick}
          />
        </div>

        <Footer t={t} destroyRef={destroyRef} activeTab={activeTab} feature={feature} />
      </div>
    </div>
  );
};
